// KIO-12: lectura del escáner de códigos de barras.
//
// Cada lectura deja un evento en activity, igual que los clics: `scan barcode-scanner add-to-cart`
// con el producto, `scan barcode-scanner open-product` si el producto tiene modificadores y se abre
// su vista para elegirlos, o `scan barcode-scanner not-found` si el código no está a la venta.
//
// SOK-17: el escáner es un teclado que escribe el código y termina con Enter. Las teclas se juntan
// en un buffer fuera de React (createBarcodeReader) para no renderizar la app en cada dígito.

export const SCAN_ENTITY_TYPE = 'barcode-scanner';
export const SCAN_ACTION = 'scan';
export const SCAN_FOUND = 'add-to-cart';
export const SCAN_OPEN_PRODUCT = 'open-product';
export const SCAN_NOT_FOUND = 'not-found';

// Motivo de un `not-found`: el código no existe en el catálogo o existe pero no está a la venta
// (sin stock, fuera de horario, modificadores inválidos…).
export const SCAN_REASON_UNKNOWN = 'unknown';
export const SCAN_REASON_UNAVAILABLE = 'unavailable';

export const SCAN_SOUND_SRC = 'sounds/beep-07a.mp3';

// Tiempo máximo entre dos teclas de una misma lectura; pasado ese tiempo se descarta lo escrito.
export const SCAN_KEY_TIMEOUT_MS = 1000;

/**
 * Limpia lo que escribió el escáner.
 *
 * @param {string} input
 * @returns {string}
 */
export function normalizeBarcode(input) {
  return String(input ?? '')
    .trim()
    .toUpperCase()
    .replace(/'/g, '-')
    .replace(/`/g, '-')
    .replace(/\s+/g, '')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Llave para comparar códigos. Los numéricos de 8 a 14 dígitos son GTIN (EAN-8, UPC-A, EAN-13,
 * GTIN-14) y se completan con ceros a 14, así un UPC-A guardado con 12 dígitos calza con la lectura
 * de 13 que antepone un 0. El EAN guardado puede venir con espacios o como número.
 *
 * @param {string|number|null|undefined} code
 * @returns {string}
 */
export function barcodeKey(code) {
  const normalized = normalizeBarcode(code);
  return /^\d{8,14}$/.test(normalized) ? normalized.padStart(14, '0') : normalized;
}

function findByBarcode(products, barcode) {
  const key = barcodeKey(barcode);
  if (!key) return null;
  return (products || []).find((item) => item?.ean != null && item.ean !== '' && barcodeKey(item.ean) === key) || null;
}

/**
 * Busca el producto por EAN y arma el evento de actividad de la lectura.
 *
 * @param {Array<object>} products - Productos válidos (a la venta) del catálogo.
 * @param {string} barcode - Código ya normalizado.
 * @param {object} [options]
 * @param {number} [options.quantity=1] - Cantidad que se agrega al carro.
 * @param {Array<object>} [options.allProducts] - Todos los productos, para distinguir un código
 *   que existe pero no está a la venta de uno desconocido.
 * @param {(product: object) => boolean} [options.hasModifiers] - Si el producto tiene
 *   modificadores que elegir antes de agregarlo.
 * @returns {{product: object|null, entityId: string, metadata: object}}
 */
export function getScanResult(products, barcode, { quantity = 1, allProducts = null, hasModifiers = null } = {}) {
  const product = findByBarcode(products, barcode);
  if (!product) {
    const unavailable = findByBarcode(allProducts, barcode);
    return {
      product: null,
      entityId: SCAN_NOT_FOUND,
      metadata: unavailable
        ? { barcode, reason: SCAN_REASON_UNAVAILABLE, product_id: unavailable.id, product_name: unavailable.label || unavailable.name || null }
        : { barcode, reason: SCAN_REASON_UNKNOWN },
    };
  }
  const metadata = {
    product_id: product.id,
    product_name: product.label || product.name || null,
    barcode,
  };
  if (hasModifiers && hasModifiers(product)) {
    return { product, entityId: SCAN_OPEN_PRODUCT, metadata };
  }
  return { product, entityId: SCAN_FOUND, metadata: { ...metadata, quantity } };
}

/**
 * Traduce una tecla del escáner a un carácter del código, 'Enter' o null (tecla que no cuenta).
 * Los dígitos se leen de `code` para no perderlos cuando el lector usa el teclado numérico con
 * Bloq Num apagado (ahí `key` llega como 'End', 'ArrowDown'…). Los atajos con Ctrl, Cmd o Alt
 * (por ejemplo pegar) no son lecturas.
 *
 * @param {KeyboardEvent} event
 * @returns {string|null}
 */
export function scanKeyFromEvent(event) {
  if (!event || event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === 'Enter' || event.code === 'NumpadEnter') return 'Enter';
  const digit = /^(?:Digit|Numpad)(\d)$/.exec(event.code || '');
  if (digit) return digit[1];
  if (typeof event.key === 'string' && event.key.length === 1) return event.key;
  return null;
}

/**
 * Junta las teclas del escáner y entrega el código al recibir Enter. Mientras `isBusy()` es
 * verdadero (una lectura en proceso) todo lo que llega se descarta, incluido el Enter, para que una
 * segunda lectura hecha durante la espera no se mezcle ni se agregue.
 *
 * @param {object} options
 * @param {(code: string) => void} options.onScan - Recibe el código ya normalizado.
 * @param {() => boolean} [options.isBusy]
 * @param {number} [options.timeout=SCAN_KEY_TIMEOUT_MS]
 * @returns {{handleKey: (key: string|null) => void, reset: () => void}}
 */
export function createBarcodeReader({ onScan, isBusy = () => false, timeout = SCAN_KEY_TIMEOUT_MS }) {
  let buffer = '';
  let timer = null;

  const reset = () => {
    buffer = '';
    clearTimeout(timer);
    timer = null;
  };

  const handleKey = (key) => {
    if (!key) return;
    if (isBusy()) {
      reset();
      return;
    }
    if (key === 'Enter') {
      const code = normalizeBarcode(buffer);
      reset();
      if (code) onScan(code);
      return;
    }
    buffer += key;
    clearTimeout(timer);
    timer = setTimeout(reset, timeout);
  };

  return { handleKey, reset };
}
